/**
 * Game glyphs (port of runebound.gg `ui/Glyphs.tsx`): domain, rarity and type symbols and the Might
 * and exhaust icons from Riot's community pack, the energy pip, the Poro line icon and holo text.
 */
import { cn } from './cn';
import { ICONS, type IconName } from './icons';

export const DOMAIN_COLOURS: Readonly<Record<string, string>> = {
  Fury: '#E0413F',
  Calm: '#3DAA6B',
  Mind: '#3F8FE0',
  Body: '#E08A3F',
  Chaos: '#B54FD6',
  Order: '#E0C13F',
  Colorless: '#9A9AAD',
};
export const RARITY_COLOURS: Readonly<Record<string, string | null>> = {
  Common: '#B8BCC8',
  Uncommon: '#5DD0C4',
  Rare: '#C061E8',
  Epic: '#F26B5E',
  Showcase: null,
  Promo: '#F5C242',
};
export const DOMAIN_ORDER = ['Fury', 'Calm', 'Mind', 'Body', 'Chaos', 'Order', 'Colorless'] as const;
export const RARITY_ORDER = ['Common', 'Uncommon', 'Rare', 'Epic', 'Showcase', 'Promo'] as const;

const domainIcon: Readonly<Record<string, IconName>> = {
  Fury: 'fury',
  Calm: 'calm',
  Mind: 'mind',
  Body: 'body',
  Chaos: 'chaos',
  Order: 'order',
  Colorless: 'rainbow-rune',
};
const filledDomainIcon: Readonly<Record<string, IconName>> = {
  Fury: 'fury2',
  Calm: 'calm2',
  Mind: 'mind2',
  Body: 'body2',
  Chaos: 'chaos2',
  Order: 'order2',
};
const rarityIcon: Readonly<Record<string, IconName>> = {
  Common: 'common',
  Uncommon: 'uncommon',
  Rare: 'rare',
  Epic: 'epic',
  Showcase: 'overnumbered',
  Promo: 'rainbow-rune-outline',
};
const typeIcon: Readonly<Record<string, IconName>> = {
  Unit: 'unit',
  Spell: 'spell',
  Gear: 'gear',
  Legend: 'legend',
  Rune: 'rune',
  Battlefield: 'battlefield',
  Champion: 'champion-unit',
};

function Icon({
  name,
  alt,
  size,
  className,
}: {
  name: IconName;
  alt: string;
  size: number;
  className?: string | undefined;
}) {
  return (
    <img
      src={ICONS[name]}
      alt={alt}
      width={size}
      height={size}
      className={cn('inline-block object-contain', className)}
    />
  );
}

/** A domain symbol (`filled` for the solid variant). */
export function DomainGlyph({
  domain,
  size = 18,
  className,
  filled,
}: {
  domain: string;
  size?: number | undefined;
  className?: string | undefined;
  filled?: boolean | undefined;
}) {
  const name = (filled ? filledDomainIcon[domain] : undefined) ?? domainIcon[domain] ?? 'rainbow-rune';
  return <Icon name={name} alt={domain} size={size} className={className} />;
}

/** A rarity symbol. */
export function RarityGlyph({
  rarity,
  size = 18,
  className,
}: {
  rarity: string;
  size?: number | undefined;
  className?: string | undefined;
}) {
  return <Icon name={rarityIcon[rarity] ?? 'common'} alt={rarity} size={size} className={className} />;
}

/** A card type symbol. */
export function TypeGlyph({
  type,
  size = 18,
  className,
}: {
  type: string;
  size?: number | undefined;
  className?: string | undefined;
}) {
  return <Icon name={typeIcon[type] ?? 'unit'} alt={type} size={size} className={className} />;
}

/** The Might symbol. */
export function MightGlyph({ size = 14, className }: { size?: number | undefined; className?: string | undefined }) {
  return <Icon name="sword" alt="Might" size={size} className={className} />;
}

/** The exhaust symbol. */
export function TapGlyph({ size = 14, className }: { size?: number | undefined; className?: string | undefined }) {
  return <Icon name="tap" alt="Exhaust" size={size} className={className} />;
}

/** Any-domain rune symbol ([A], `:rb_rune_rainbow:`). */
export function AnyRuneGlyph({ size = 16, className }: { size?: number | undefined; className?: string | undefined }) {
  return <Icon name="rainbow-rune" alt="any rune" size={size} className={className} />;
}

/** Energy cost circle. */
export function EnergyPip({
  value,
  size = 22,
  className,
}: {
  value: number | string;
  size?: number | undefined;
  className?: string | undefined;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-surface3 border border-border-strong mono font-medium',
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.55) }}
    >
      {value}
    </span>
  );
}

/** The Poro (monocle) mascot, path for path from runebound.gg (research 06 §6), stroked in `currentColor`. */
export function PoroIcon({
  size = 22,
  className,
  strokeWidth = 29,
}: {
  size?: number | undefined;
  className?: string | undefined;
  strokeWidth?: number | undefined;
}) {
  const face = strokeWidth * (25 / 29);
  return (
    <svg width={size} height={size} viewBox="160 0 1216 1024" fill="none" className={className} aria-hidden>
      <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        <path d="M 520,923 C 426,923 355,874 385,797 C 358,756 363,704 388,670 C 355,661 349,642 402,630 C 357,602 373,560 411,516 C 399,482 428,462 480,425 C 504,352 558,260 608,218 C 625,203 633,233 640,261 C 698,181 769,142 831,134 C 847,132 845,142 839,162 L 831,253 C 868,217 911,183 924,202 C 946,238 962,353 1007,419 C 1075,462 1124,505 1124,550 C 1171,580 1158,614 1091,640 C 1137,677 1142,730 1108,797 C 1149,856 1110,923 1016,923" />
        <path d="M 500,365 C 438,401 400,397 369,378 C 331,354 343,322 369,294 C 384,277 371,278 358,281 C 300,292 263,337 246,389 C 222,462 270,519 347,543 C 374,552 394,541 411,516" />
        <path d="M 987,365 C 1079,409 1136,398 1167,378 C 1205,354 1193,322 1167,294 C 1152,277 1165,278 1178,281 C 1236,292 1273,337 1290,389 C 1314,462 1266,519 1189,543 C 1162,552 1139,542 1110,520" />
        <path d="M 520,923 H 1016" />
      </g>
      <g stroke="currentColor" strokeWidth={face} strokeLinecap="round" strokeLinejoin="round">
        <path d="M 516,659 C 552,615 621,617 675,662" />
        <path d="M 654,742 C 647,766 661,786 686,790 C 710,794 734,781 750,766 C 768,782 795,795 819,782 C 837,772 845,756 843,744" />
        <circle cx="920" cy="639" r="105" />
      </g>
    </svg>
  );
}

/** Text with the holographic gradient sliding through it. */
export function HoloText({ children, className }: { children: React.ReactNode; className?: string | undefined }) {
  return <span className={cn('holo-text', className)}>{children}</span>;
}
